import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const ProjectContext = createContext(null);

export function ProjectProvider({ children }) {
  const { user } = useAuth();
  const [allProjects, setAllProjects] = useState([]);
  const [projects, setProjects] = useState([]); // filtered to active selection
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeProjectIds, setActiveProjectIds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load projects list
  const fetchProjects = useCallback(async () => {
    if (!user) {
      setAllProjects([]);
      setProjects([]);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/projects');
      const list = Array.isArray(res.data) ? res.data : res.data?.projects || [];
      setAllProjects(list);

      // Filter to only projects selected during login
      const storedIds = JSON.parse(localStorage.getItem('ciq_active_projects') || '[]');
      const activeIds = storedIds.length > 0 ? storedIds : list.map(p => p.project_id);
      setActiveProjectIds(activeIds);

      const filtered = activeIds.length > 0 && storedIds.length > 0
        ? list.filter(p => activeIds.includes(p.project_id))
        : list;
      setProjects(filtered);

      // Restore persisted selection or auto-select first from filtered
      const saved = localStorage.getItem('ciq_selected_project');
      const match = saved ? filtered.find(p => p.project_id === saved) : null;
      if (match) {
        setSelectedProject(match);
      } else if (filtered.length > 0) {
        setSelectedProject(filtered[0]);
        localStorage.setItem('ciq_selected_project', filtered[0].project_id);
      }
    } catch {
      setAllProjects([]);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Listen for changes to active project selection (from ProjectSelector page)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'ciq_active_projects') {
        fetchProjects();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [fetchProjects]);

  const selectProject = useCallback((projectId) => {
    // Search in all projects first, then filtered
    const p = allProjects.find(pr => pr.project_id === projectId) || projects.find(pr => pr.project_id === projectId);
    if (p) {
      setSelectedProject(p);
      localStorage.setItem('ciq_selected_project', projectId);
    } else {
      // If selecting from ProjectSelector before projects are loaded
      localStorage.setItem('ciq_selected_project', projectId);
    }
  }, [allProjects, projects]);

  // Update active projects (called from ProjectSelector)
  const setActiveProjects = useCallback((ids) => {
    localStorage.setItem('ciq_active_projects', JSON.stringify(ids));
    setActiveProjectIds(ids);
    const filtered = ids.length > 0
      ? allProjects.filter(p => ids.includes(p.project_id))
      : allProjects;
    setProjects(filtered);
  }, [allProjects]);

  return (
    <ProjectContext.Provider value={{
      projects,         // filtered to active selection
      allProjects,      // all projects user has access to
      activeProjectIds, // IDs of projects selected during login
      selectedProject,
      projectId: selectedProject?.project_id || null,
      projectName: selectedProject?.name || selectedProject?.projectName || '',
      selectProject,
      setActiveProjects,
      loading,
      refreshProjects: fetchProjects,
    }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be inside ProjectProvider');
  return ctx;
}
